import React, { useMemo } from 'react';
import {
  TeacherApplicationForm,
  DAYS_OF_WEEK,
  TIME_SLOTS,
  DAY_LABELS,
  TIME_SLOT_LABELS,
  DayOfWeek,
  TimeSlot,
  getAvailabilityFieldName,
} from '../../model/TeacherApplicationForm';
import { countSelectedAvailability } from '../../utils/teacherApplicationValidators';

interface AvailabilityStepProps {
  formData: TeacherApplicationForm;
  setFormData: (data: TeacherApplicationForm) => void;
  errors: { [key: string]: string };
  setErrors: (errors: { [key: string]: string }) => void;
}

const AvailabilityStep: React.FC<AvailabilityStepProps> = ({ formData, setFormData, errors, setErrors }) => {
  const selectedCount = useMemo(() => countSelectedAvailability(formData), [formData]);

  const handleToggle = (day: DayOfWeek, timeSlot: TimeSlot) => {
    const fieldName = getAvailabilityFieldName(day, timeSlot);
    setFormData({
      ...formData,
      [fieldName]: !formData[fieldName],
    });

    // Clear error if at least one is selected
    if (errors.availability && selectedCount >= 0) {
      setErrors({ ...errors, availability: '' });
    }
  };

  const toggleColumn = (day: DayOfWeek) => {
    const allSelected = TIME_SLOTS.every((slot) => {
      const fieldName = getAvailabilityFieldName(day, slot) as keyof TeacherApplicationForm;
      return formData[fieldName] === true;
    });

    const updatedData = { ...formData };
    TIME_SLOTS.forEach((slot) => {
      const fieldName = getAvailabilityFieldName(day, slot) as keyof TeacherApplicationForm;
      (updatedData[fieldName] as boolean) = !allSelected;
    });

    setFormData(updatedData);
    setErrors({ ...errors, availability: '' });
  };

  const toggleRow = (timeSlot: TimeSlot) => {
    const allSelected = DAYS_OF_WEEK.every((day) => {
      const fieldName = getAvailabilityFieldName(day, timeSlot) as keyof TeacherApplicationForm;
      return formData[fieldName] === true;
    });

    const updatedData = { ...formData };
    DAYS_OF_WEEK.forEach((day) => {
      const fieldName = getAvailabilityFieldName(day, timeSlot) as keyof TeacherApplicationForm;
      (updatedData[fieldName] as boolean) = !allSelected;
    });

    setFormData(updatedData);
    setErrors({ ...errors, availability: '' });
  };

  const toggleAll = () => {
    const allSelected = selectedCount === 56;
    const updatedData = { ...formData };

    DAYS_OF_WEEK.forEach((day) => {
      TIME_SLOTS.forEach((slot) => {
        const fieldName = getAvailabilityFieldName(day, slot) as keyof TeacherApplicationForm;
        (updatedData[fieldName] as boolean) = !allSelected;
      });
    });

    setFormData(updatedData);
    setErrors({ ...errors, availability: '' });
  };

  const isColumnAllSelected = (day: DayOfWeek) => {
    return TIME_SLOTS.every((slot) => {
      const fieldName = getAvailabilityFieldName(day, slot);
      return formData[fieldName] === true;
    });
  };

  const isRowAllSelected = (timeSlot: TimeSlot) => {
    return DAYS_OF_WEEK.every((day) => {
      const fieldName = getAvailabilityFieldName(day, timeSlot);
      return formData[fieldName] === true;
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div>
            <p className="text-sm font-medium text-blue-900">
              Fasce selezionate: <span className="font-bold text-lg">{selectedCount}/56</span>
            </p>
            <p className="text-xs text-blue-700 mt-1">Seleziona almeno una fascia oraria</p>
          </div>
          <button
            type="button"
            onClick={toggleAll}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
            {selectedCount === 56 ? 'Deseleziona tutto' : 'Seleziona tutto'}
          </button>
        </div>
      </div>

      {/* Desktop Grid */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="border border-gray-300 bg-gray-100 p-3 text-sm font-semibold text-gray-700 sticky left-0 z-10">
                Fascia Oraria
              </th>
              {DAYS_OF_WEEK.map((day) => (
                <th key={day} className="border border-gray-300 bg-gray-100 p-2">
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">{DAY_LABELS[day]}</span>
                    <button
                      type="button"
                      onClick={() => toggleColumn(day)}
                      className={`text-xs px-2 py-1 rounded transition-colors ${
                        isColumnAllSelected(day)
                          ? 'bg-fpcred text-white hover:bg-red-700'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}>
                      {isColumnAllSelected(day) ? 'Desel.' : 'Sel. tutto'}
                    </button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TIME_SLOTS.map((timeSlot) => (
              <tr key={timeSlot}>
                <td className="border border-gray-300 bg-gray-50 p-2 sticky left-0 z-10">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-700">{TIME_SLOT_LABELS[timeSlot]}</span>
                    <button
                      type="button"
                      onClick={() => toggleRow(timeSlot)}
                      className={`text-xs px-2 py-1 rounded transition-colors whitespace-nowrap ${
                        isRowAllSelected(timeSlot)
                          ? 'bg-fpcred text-white hover:bg-red-700'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}>
                      {isRowAllSelected(timeSlot) ? 'Desel.' : 'Sel. riga'}
                    </button>
                  </div>
                </td>
                {DAYS_OF_WEEK.map((day) => {
                  const fieldName = getAvailabilityFieldName(day, timeSlot);
                  const isSelected = formData[fieldName];
                  return (
                    <td key={`${day}-${timeSlot}`} className="border border-gray-300 p-2 text-center">
                      <button
                        type="button"
                        onClick={() => handleToggle(day, timeSlot)}
                        className={`w-12 h-12 rounded-lg transition-all transform hover:scale-110 ${
                          isSelected ? 'bg-fpcred text-white shadow-md' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        }`}
                        aria-label={`${DAY_LABELS[day]} ${TIME_SLOT_LABELS[timeSlot]}`}>
                        {isSelected ? '✓' : ''}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Accordion */}
      <div className="lg:hidden space-y-3">
        {DAYS_OF_WEEK.map((day) => {
          const daySelectedCount = TIME_SLOTS.filter((slot) => {
            const fieldName = getAvailabilityFieldName(day, slot);
            return formData[fieldName] === true;
          }).length;

          return (
            <details key={day} className="border border-gray-300 rounded-lg">
              <summary className="bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 flex items-center justify-between">
                <div>
                  <span className="font-semibold text-gray-800">{DAY_LABELS[day]}</span>
                  <span className="ml-2 text-sm text-gray-600">({daySelectedCount}/8 fasce)</span>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleColumn(day);
                  }}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    isColumnAllSelected(day) ? 'bg-fpcred text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}>
                  {isColumnAllSelected(day) ? 'Deseleziona' : 'Seleziona tutto'}
                </button>
              </summary>
              <div className="p-4 space-y-2">
                {TIME_SLOTS.map((timeSlot) => {
                  const fieldName = getAvailabilityFieldName(day, timeSlot);
                  const isSelected = formData[fieldName];
                  return (
                    <button
                      key={timeSlot}
                      type="button"
                      onClick={() => handleToggle(day, timeSlot)}
                      className={`w-full p-3 rounded-lg text-left transition-all ${
                        isSelected ? 'bg-fpcred text-white font-medium' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}>
                      <div className="flex items-center justify-between">
                        <span>{TIME_SLOT_LABELS[timeSlot]}</span>
                        {isSelected && <span className="text-xl">✓</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </details>
          );
        })}
      </div>

      {errors.availability && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-sm text-red-700">{errors.availability}</p>
        </div>
      )}
    </div>
  );
};

export default AvailabilityStep;
